import { useEffect, useRef, useState } from 'react'
import { Container, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'
import { Circles } from 'react-loader-spinner'
import { Document, Page, pdfjs } from 'react-pdf'
import { RiDeleteBinLine, RiPrinterLine } from 'react-icons/ri'
import { Link } from 'react-router-dom'
import { iIcon, PrintIcon, qr, shoppingCartIcon } from '../../../assests/Icons/icon'
import { downloadPDF, notifyBugsnagError, openQrCode, printPDF, removeDashed, showToast } from '../../../common/utils/Functions'
import { initPdfWorker } from '../../../common/utils/pdfWorker'
import DashboardLayout from '../../../components/layouts/DashboardLayout'
import ConfirmationModal from '../../../components/modals/ConfirmationModal/ConfirmationModal'
import PageHeading from '../../../components/pageHeading'
import SignsModal from '../../../components/pagesComponents/signs/SignsModal'
import ReactTable from '../../../components/reactTable/ReactTable'
import ThemeButton from '../../../components/theme/ThemeButton'
import useFetch from '../../../hooks/useFetch'
import styles from './signs.module.scss'

initPdfWorker();

const Signs = () => {
    const [showSignForm, setShowSignForm] = useState<boolean>(false);
    const [signList, setSignList] = useState<any[]>();
    const [selectedItem, setSelectedItem] = useState<any>();
    const [openDelete, setOpenDelete] = useState<boolean>(false);

    // Thumbnail states — mirrors tipping experience wizard step 2
    const [mostRecentSign, setMostRecentSign] = useState<{
        pdfUrl: string;
        signName: string;
        sizeName: string;
        linkToBuy?: string;
        purchaseSteps?: string;
    } | null>(null);
    const [signPdfLoading, setSignPdfLoading] = useState(false);
    const [signPdfError, setSignPdfError] = useState<string>("");
    const [useIframePreview, setUseIframePreview] = useState(false);
    const [printDisabled, setPrintDisabled] = useState(false);
    const [downloadDisabled, setDownloadDisabled] = useState(false);
    const [pdfWidth, setPdfWidth] = useState(380);
    const signCardRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = signCardRef.current;
        if (!el) return;
        const compute = () => {
            setPdfWidth(Math.floor(el.getBoundingClientRect().width) - 2);
        };
        const ro = new ResizeObserver(compute);
        ro.observe(el);
        compute();
        return () => ro.disconnect();
    }, [mostRecentSign]);

    // Reset PDF loading state when URL changes
    useEffect(() => {
        if (mostRecentSign?.pdfUrl) {
            setSignPdfLoading(true);
            setSignPdfError("");
            setUseIframePreview(false);
        } else {
            setSignPdfLoading(false);
            setSignPdfError("PDF URL not available");
            setUseIframePreview(false);
        }
    }, [mostRecentSign?.pdfUrl]);

    const handlePrintClick = () => {
        if (!mostRecentSign?.pdfUrl || printDisabled) return;
        setPrintDisabled(true);
        Promise.resolve(printPDF(mostRecentSign.pdfUrl)).finally(() => {
            setTimeout(() => setPrintDisabled(false), 2000);
        });
    };

    const handleDownloadClick = () => {
        if (!mostRecentSign?.pdfUrl || downloadDisabled) return;
        setDownloadDisabled(true);
        Promise.resolve(downloadPDF(mostRecentSign.pdfUrl, "sign.pdf")).finally(() => {
            setTimeout(() => setDownloadDisabled(false), 2000);
        });
    };

    const [pageLimit, setPageLimit] = useState<number>(10);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);

    const toggleAddSign = () => setShowSignForm(!showSignForm);
    const handleDelete = () => setOpenDelete(false);

    const [signsList, { response: signsListRes, error: signsListErr, loading: signsListLoading }] =
        useFetch("/common/qr-sign/list", { method: "post" });

    const [
        deleteApi,
        { response: deleteResponse, loading: deleteLoading, error: deleteError },
    ] = useFetch(`/common/qr-sign/${selectedItem?.uuid}`, { method: "delete" });

    useEffect(() => {
        if (deleteResponse) {
            showToast(deleteResponse.message as string, "success");
            try {
                signsList({ start: (currentPage - 1) * pageLimit, limit: pageLimit, is_active: null });
            } catch (e: any) {
                notifyBugsnagError(e, { api: "signsList" });
            }
            handleDelete();
        }
        if (deleteError) {
            showToast(deleteError.message as string, "error");
        }
    }, [deleteResponse, deleteError]);

    useEffect(() => {
        const timer = setTimeout(() => {
            try {
                signsList({ start: (currentPage - 1) * pageLimit, limit: pageLimit, is_active: null });
            } catch (e: any) {
                notifyBugsnagError(e, { api: "signsList" });
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [currentPage, pageLimit]);

    const description = (item: any) => {
        if (item.sign_type === 1) {
            return item?.size_details?.name && item?.sign_details?.name
                ? `${item.size_details.name} ${item.sign_details.name} for ${item.organization__details.name}`
                : removeDashed;
        } else if (item.sign_type === 2) {
            return item?.size_details?.name && item?.sign_details?.name
                ? `${item.size_details.name} ${item.sign_details.name} for ${item.property__details.name}`
                : removeDashed;
        } else if (item.sign_type === 3) {
            return item?.size_details?.name && item?.sign_details?.name
                ? `${item.size_details.name} ${item.sign_details.name} for ${item.property_service__details.name}`
                : removeDashed;
        } else if (item.sign_type === 4) {
            return item?.size_details?.name && item?.sign_details?.name
                ? `${item.size_details.name} ${item.sign_details.name} for ${item.property_room__details.name}`
                : removeDashed;
        } else if (item.sign_type === 5) {
            return item?.size_details?.name && item?.sign_details?.name
                ? `${item.size_details.name} ${item.sign_details.name} for ${item.employee__details.name}`
                : removeDashed;
        }
    };

    useEffect(() => {
        if (signsListRes) {
            setTotalRecords(signsListRes.data?.totalRecords);
            const rawList = signsListRes.data.list;
            if (rawList && rawList.length > 0) {
                const first = rawList[0];
                const pdfUrl = first?.url || first?.sign_pdf_url || first?.file_url || first?.fileUrl || "";
                setMostRecentSign({
                    pdfUrl,
                    signName: first?.sign_details?.name ?? "",
                    sizeName: first?.size_details?.name ?? "",
                    linkToBuy: first?.sign_details?.link_to_buy,
                    purchaseSteps: first?.sign_details?.purchase_steps,
                });
            } else {
                setMostRecentSign(null);
            }
            const updatedSignList = rawList?.map((item: any) => {
                const pdfUrl = item?.url || item?.sign_pdf_url || item?.file_url || item?.fileUrl || "";
                return {
                    description: description(item),
                    url: (
                        <span onClick={() => { if (pdfUrl) openQrCode(pdfUrl); }}>
                            {" "}{qr}{" "}
                        </span>
                    ),
                    uuid: item?.uuid,
                    pdfUrl,
                    linkToBuy: (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                            <Link to={item?.sign_details?.link_to_buy} target="_blank" rel="noopener noreferrer">
                                {shoppingCartIcon}
                            </Link>
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip id={`tooltip-${item.id}`}>
                                        <div style={{ textAlign: 'left' }} dangerouslySetInnerHTML={{ __html: item?.sign_details?.purchase_steps }} />
                                    </Tooltip>
                                }
                            >
                                <span style={{ cursor: 'pointer' }}>{iIcon}</span>
                            </OverlayTrigger>
                        </span>
                    ),
                };
            });
            setSignList(updatedSignList);
        }
        if (signsListErr) {
            showToast(signsListErr.message as string, "error");
        }
    }, [signsListRes, signsListErr]);

    const signHeading = [
        { key: "description", label: "Sign Description" },
        { key: "url", label: "Download", textAlign: 'center' },
        { key: 'linkToBuy', label: 'Link To Buy', textAlign: 'center' },
    ];

    const actionButtonOptions = [
        {
            name: "Delete",
            icon: <RiDeleteBinLine />,
            onClick: (item: any) => { setSelectedItem(item); setOpenDelete(true); },
        },
        {
            name: "Print",
            icon: <RiPrinterLine />,
            onClick: (item: any) => {
                setSelectedItem(item);
                const pdfUrl = item?.pdfUrl || "";
                if (typeof pdfUrl === "string" && pdfUrl.trim()) {
                    printPDF(pdfUrl);
                } else {
                    showToast("No PDF available for this sign.", "error");
                }
            },
        },
    ];

    return (
        <>
            <DashboardLayout>
                <Container className={styles.Container}>
                    <div className={styles.dash_page}>
                        <PageHeading
                            heading="Signs"
                            subHeading="Here you can see all of your created signs."
                        >
                            <ThemeButton onClick={() => toggleAddSign()}>Create a Sign</ThemeButton>
                        </PageHeading>
                    </div>

                    {/* Most recent sign thumbnail — exact copy of wizard step 2 sign preview */}
                    {mostRecentSign && (
                        <div className={styles.signHintGroup}>
                        <div className={styles.signPreview}>
                            <div className={styles.signCard} ref={signCardRef}>
                                <div className={styles.pdfWrapper}>
                                    {signPdfLoading && (
                                        <div className={styles.pdfLoader}>
                                            <Circles height="40" width="40" color="#2549E8" ariaLabel="pdf-loading" visible />
                                        </div>
                                    )}
                                    {mostRecentSign.pdfUrl ? (
                                        useIframePreview ? (
                                            <iframe
                                                src={`${mostRecentSign.pdfUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                                                title="Sign PDF Preview"
                                                className={styles.pdfIframe}
                                                onLoad={() => { setSignPdfLoading(false); setSignPdfError(""); }}
                                            />
                                        ) : (
                                            <Document
                                                key={mostRecentSign.pdfUrl}
                                                file={mostRecentSign.pdfUrl}
                                                onLoadSuccess={() => { setSignPdfLoading(false); setSignPdfError(""); }}
                                                onLoadError={(error) => {
                                                    setSignPdfLoading(false);
                                                    setUseIframePreview(true);
                                                    setSignPdfError("Failed to load PDF preview");
                                                    notifyBugsnagError(error as any, { page: "signs", action: "loadSignPdfPreview", pdfUrl: mostRecentSign.pdfUrl });
                                                }}
                                                loading={null}
                                            >
                                                <Page
                                                    pageNumber={1}
                                                    width={pdfWidth}
                                                    renderTextLayer={false}
                                                    renderAnnotationLayer={false}
                                                />
                                            </Document>
                                        )
                                    ) : (
                                        !signPdfLoading && (
                                            <div className={styles.pdfUnavailable}>
                                                <p>{signPdfError || "PDF not available"}</p>
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                            <div className={styles.signActions}>
                                <div className={styles.signButtons}>
                                    <button
                                        type="button"
                                        className={styles.printButton}
                                        onClick={handlePrintClick}
                                        disabled={!mostRecentSign.pdfUrl || printDisabled}
                                    >
                                        {PrintIcon}
                                        Print Sign
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.printButton}
                                        onClick={handleDownloadClick}
                                        disabled={!mostRecentSign.pdfUrl || downloadDisabled}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M13 5V11H14.17L12 13.17L9.83 11H11V5H13ZM15 3H9V9H5L12 16L19 9H15V3ZM19 18H5V20H19V18Z" fill="white" />
                                        </svg>
                                        Download Sign
                                    </button>
                                </div>
                                {(mostRecentSign.signName || mostRecentSign.sizeName) && (
                                    <div className={styles.signMeta}>
                                        {mostRecentSign.signName && (
                                            <span className={styles.signMetaName}>{mostRecentSign.signName}</span>
                                        )}
                                        {mostRecentSign.sizeName && (
                                            <span className={styles.signMetaSize}>{mostRecentSign.sizeName}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                            <div className={styles.signHintCard}>
                                <p className={styles.signHintText}>Want to customize your sign? You can download it here and upload it into an editor like Canva</p>
                            </div>
                            <svg
                                className={styles.signHintArrow}
                                viewBox="0 0 375 240"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <defs>
                                    <marker id="signArrowHead" markerWidth="10" markerHeight="8" refX="9" refY="4" orient="auto">
                                        <path d="M0,0 L10,4 L0,8 Z" fill="#2549E8" />
                                    </marker>
                                </defs>
                                <path
                                    d="M 290,80 C 290,190 60,170 5,170"
                                    stroke="#2549E8"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeDasharray="6 5"
                                    markerEnd="url(#signArrowHead)"
                                />
                            </svg>
                        </div>
                    )}

                    <Row>
                        <ReactTable
                            headings={signHeading}
                            data={signList || []}
                            actionColumn
                            actionButtonOptions={actionButtonOptions}
                            currentPage={currentPage}
                            itemPerPage={pageLimit}
                            totalItems={totalRecords}
                            setPerPageLimit={setPageLimit}
                            pageLimit={pageLimit}
                            setCurrentPage={setCurrentPage}
                            loading={signsListLoading || !signList}
                            pagination={true}
                        >
                            <div className={styles.org}>
                                <p>Signs</p>
                            </div>
                        </ReactTable>
                        {!signsListLoading && signList ?
                            <p style={{ textAlign: 'right' }}>For assistance, or to request a sign style or size not offered, please email <a href='mailto:support@toptipper.co '>support@toptipper.co</a></p> : ''
                        }
                    </Row>

                    <SignsModal
                        show={showSignForm}
                        handleClose={toggleAddSign}
                        title={"Create A New Sign"}
                        signsList={signsList}
                    />

                    <ConfirmationModal
                        handleToggle={handleDelete}
                        title="Are you sure you want to delete this sign?"
                        show={openDelete}
                        loading={deleteLoading}
                        confirm={() => deleteApi()}
                    />
                </Container>
            </DashboardLayout>
        </>
    );
};

export default Signs;
